import React, {useEffect, useRef, useState, useMemo, useContext} from 'react'
import PropTypes from 'prop-types'
import {useSearchSuggestions} from '@salesforce/commerce-sdk-react'
import {useIntl, defineMessage} from 'react-intl'
import {
    Input,
    InputGroup,
    InputRightElement,
    IconButton,
    Box,
    Flex,
    HStack,
    Stack,
    VisuallyHidden,
    Text,
    Button
} from '@salesforce/retail-react-app/app/components/shared/ui'
import SearchSuggestions from './partials/search-suggestions'
import {CloseIcon, SearchIcon} from '../custom-icons'
import LoadingSpinner from '../loading-spinner'

import {
    productUrlBuilder,
    searchUrlBuilder,
    categoryUrlBuilder
} from '@salesforce/retail-react-app/app/utils/url'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {pageUrlBuilder} from '../../utils/url-utils'
import {RECENT_SEARCH_MIN_LENGTH} from '@salesforce/retail-react-app/app/constants'
import {togglePageScroll} from '../../utils/utils'
import {useXSSProtection} from './../../hooks/use-xss-protection.js'
import {useToast} from '../../hooks/use-toast'
import {IPContext, useSearchRedirects} from '../../custom-contexts'
import useMultiSite from '../../hooks/use-multi-site'
import {useHistory} from 'react-router-dom'
import {productSetURLBuilder} from '../../utils/seo-utils.js'
import {OUTLET_DISPATCH_URL} from '../../constants.js'
import {findSearchRedirect, resolveSearchRedirect} from '../../utils/search-redirects'

const SEARCH_PLACEHOLDER = defineMessage({
    id: 'header.field.placeholder.search',
    defaultMessage: 'Search'
})

/**
 * Processes search suggestions and categorizes them into types.
 *
 * @param {Object} searchSuggestions - The search suggestions object.
 * @param {Object[]} [searchSuggestions.categorySuggestions.categories] - Array of category suggestions.
 * @param {string} searchSuggestions.categorySuggestions.categories[].id - The ID of the category.
 * @param {string} searchSuggestions.categorySuggestions.categories[].name - The name of the category.
 * @param {Object[]} [searchSuggestions.productSuggestions.products] - Array of product suggestions.
 * @param {string} searchSuggestions.productSuggestions.products[].productId - The ID of the product.
 * @param {string} searchSuggestions.productSuggestions.products[].productName - The name of the product.
 * @param {Object[]} [searchSuggestions.contentSuggestions.c_pdSuggestions] - Array of content page suggestions.
 * @param {string} searchSuggestions.contentSuggestions.c_pdSuggestions[].id - The ID of the content page.
 * @param {string} searchSuggestions.contentSuggestions.c_pdSuggestions[].name - The name of the content page.
 *
 * @returns {Object} An object containing categorized suggestions.
 * @returns {Object[]} return.categories - Array of formatted category suggestions.
 * @returns {Object[]} return.products - Array of formatted product suggestions.
 * @returns {Object[]} return.pages - Array of formatted content page suggestions.
 */
const handleSearch = (searchSuggestions) => {
    let suggestionTypes = {
        categories: searchSuggestions?.categorySuggestions?.categories?.map((suggestion) => {
            return {
                type: 'category',
                id: suggestion.id,
                link: categoryUrlBuilder({id: suggestion.id}),
                name: suggestion.name
            }
        }),
        /*
            Use extendedProductSuggestions to check if the product is a product set
            and use the productSetURLBuilder to build the link
        */
        products: searchSuggestions?.productSuggestions?.c_extendedProductSuggestions
            ?.map((product) => {
                if (product.productName && product.price !== null && !isNaN(product.price)) {
                    if (product?.isProductSet === true) {
                        return {
                            type: 'product',
                            productId: product.productId,
                            name: product.productName,
                            link: productSetURLBuilder(product.productId)
                        }
                    }
                    return {
                        type: 'product',
                        productId: product.productId,
                        name: product.productName,
                        link: productUrlBuilder({id: product.productId})
                    }
                }
                return null // remove products with missing data
            })
            .filter((product) => product !== null), // remove any null products
        pages: searchSuggestions?.contentSuggestions?.c_pdSuggestions?.map((page) => {
            return {
                type: 'page',
                id: page.id,
                name: page.name,
                link: pageUrlBuilder(page.id)
            }
        })
    }

    return suggestionTypes
}

/**
 * Search component that provides a search input with suggestions.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {boolean} [props.isSuggestionsEnabled=true] - Flag to enable or disable search suggestions
 * @param {function} props.onClose - Function to call when the close button is clicked.
 *
 * @returns {JSX.Element} The rendered component.
 */

const Search = ({
    isSuggestionsEnabled = true,
    isFocusOnInit = true,
    onClose,
    containerProps,
    ...props
}) => {
    const navigate = useNavigation()
    const intl = useIntl()
    const [distance, setDistance] = useState(0)
    const [searchQuery, setSearchQuery] = useState('')
    const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false)
    const [suggestionsLength, setSuggestionsLength] = useState(false)
    const toast = useToast()
    const [isXssError, setIsXssError] = useState(false)
    const {isIPBlocked} = useContext(IPContext)

    const searchInputRef = useRef()
    const {buildUrl, isOutletSite} = useMultiSite()
    const history = useHistory()
    const searchRedirects = useSearchRedirects()

    /**
     * Focuses the search input element if it is currently available.
     *
     * This function checks if the `searchInputRef` is defined and points to a valid DOM element,
     * then calls the `focus` method on that element to bring it into focus.
     */
    const focusInputRef = () => {
        searchInputRef.current?.focus()
    }

    useEffect(() => {
        if (isFocusOnInit) {
            focusInputRef()
        }
    }, [])

    const searchSuggestion = useSearchSuggestions(
        {
            parameters: {
                q: searchQuery,
                limit: 10
            }
        },
        {
            enabled: isSuggestionsEnabled && searchQuery?.length >= RECENT_SEARCH_MIN_LENGTH
        }
    )
    const searchSuggestions = useMemo(
        () => (isSuggestionsEnabled ? handleSearch(searchSuggestion.data) : {}),
        [searchSuggestion, isSuggestionsEnabled]
    )

    useEffect(() => {
        if (isSuggestionsEnabled) {
            const calculateDistance = () => {
                if (searchInputRef.current) {
                    const rect = searchInputRef.current.getBoundingClientRect()
                    const distanceFromBottom = window.innerHeight - rect.bottom
                    setDistance(distanceFromBottom)
                }
            }
            calculateDistance()
            const handleResizeScrollClick = () => {
                calculateDistance()
            }
            window.addEventListener('resize', handleResizeScrollClick)
            window.addEventListener('scroll', handleResizeScrollClick)
            window.addEventListener('click', handleResizeScrollClick)
            return () => {
                window.removeEventListener('resize', handleResizeScrollClick)
                window.removeEventListener('scroll', handleResizeScrollClick)
                window.removeEventListener('click', handleResizeScrollClick)
            }
        }
    }, [isSuggestionsEnabled])

    useEffect(() => {
        if (isSuggestionsEnabled) {
            setSuggestionsLength(
                (searchSuggestions.products?.length || 0) +
                    (searchSuggestions.categories?.length || 0) +
                    (searchSuggestions.pages?.length || 0)
            )
        }
    }, [searchSuggestions, isSuggestionsEnabled])

    useEffect(() => {
        togglePageScroll(!isSuggestionsOpen)
    }, [isSuggestionsOpen])

    const {validateXssContent} = useXSSProtection()

    const onSearchInputChange = async (e) => {
        if (isSuggestionsEnabled) {
            let inputValue = e.target.value
            const isXssDetected = await validateXssContent(
                inputValue,
                'search-form',
                'search-input',
                true
            )
            if (inputValue.length >= RECENT_SEARCH_MIN_LENGTH) {
                setIsSuggestionsOpen(true)
                setSearchQuery(inputValue)
                if (isXssDetected) {
                    setIsXssError(true)
                    return
                } else {
                    setIsXssError(false)
                }
            } else {
                setIsSuggestionsOpen(false)
                setSearchQuery('')
            }
        }
    }

    const onSubmitSearch = async (e) => {
        e.preventDefault()
        if (isIPBlocked) {
            const path = buildUrl(isOutletSite ? OUTLET_DISPATCH_URL : '/')
            history.push(path)
            return
        }

        let searchText = searchInputRef.current.value.trim()
        const isXssDetected = await validateXssContent(searchText, 'search-form', 'search-input')
        if (isXssDetected) {
            toast({
                title: intl.formatMessage({
                    defaultMessage: 'web links of any sort are refused',
                    id: 'use_form_fields.error.xss_detected'
                }),
                status: 'error'
            })
            navigate(searchUrlBuilder(''))
            return
        }

        if (searchText.length < 1) {
            return
        }

        // Redirect the shopper when the query matches an SFCC search redirect.
        const matchedRedirect = findSearchRedirect(searchRedirects, searchText)
        const redirectTarget = matchedRedirect ? resolveSearchRedirect(matchedRedirect) : null

        if (matchedRedirect && !redirectTarget) {
            // e.g. legacy `pipeline` redirects are not supported in PWA Kit.
            console.warn(
                `Search redirect for "${matchedRedirect.term}" has unsupported type "${matchedRedirect.type}"; showing search results instead.`
            )
        }

        // Call onClose only if suggestions are enabled
        if (isSuggestionsEnabled) {
            onClose()
        }

        if (redirectTarget) {
            if (redirectTarget.external) {
                window.location.assign(redirectTarget.path)
            } else {
                navigate(redirectTarget.path)
            }
            return
        }

        navigate(searchUrlBuilder(searchText))
    }

    /**
     * Handles tab navigation within the search input.
     *
     * This function prevents the default tab behavior when the Tab key is pressed
     * without the Shift key, and refocuses the search input element. This is useful
     * for maintaining focus within the search input when navigating through the suggestions.
     *
     * @param {KeyboardEvent} e - The keyboard event triggered by the user's key press.
     */
    const handleTabNavigation = (e) => {
        if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault()
            focusInputRef()
        }
    }

    return (
        <Box position={'relative'} {...containerProps}>
            <form onSubmit={onSubmitSearch}>
                <HStack>
                    <InputGroup>
                        <Input
                            aria-label={intl.formatMessage(SEARCH_PLACEHOLDER)}
                            placeholder={intl.formatMessage(SEARCH_PLACEHOLDER)}
                            autoComplete="off"
                            id="search-input"
                            type="search"
                            {...(isSuggestionsEnabled && {
                                borderWidth: '0',
                                borderTopWidth: '1px',
                                borderBottomWidth: '1px',
                                onChange: onSearchInputChange,
                                onKeyDown: (e) => {
                                    if (e.key === 'Tab' && e.shiftKey) {
                                        e.preventDefault()
                                        onClose()
                                    }
                                }
                            })}
                            {...(!isSuggestionsEnabled && {paddingRight: '50px'})}
                            fontSize={{base: '16px', lg: '12px'}}
                            sx={{
                                '&::-webkit-search-cancel-button': {display: 'none'},
                                '&::placeholder': {fontSize: '11px'}
                            }}
                            ref={searchInputRef}
                            {...props}
                            variant="basic"
                            defaultValue={''}
                            {...(isSuggestionsEnabled && {
                                'aria-describedby': 'searchInputDescription'
                            })}
                        />
                        {isSuggestionsEnabled && (
                            <VisuallyHidden id="searchInputDescription">
                                {intl.formatMessage({
                                    defaultMessage:
                                        'The following text field filters the results that follow as you type.',
                                    id: 'search.input.description'
                                })}
                            </VisuallyHidden>
                        )}
                        <InputRightElement>
                            {isSuggestionsEnabled ? (
                                <IconButton
                                    icon={<CloseIcon boxSize="12px" />}
                                    size="sm"
                                    onClick={onClose}
                                    aria-label={intl.formatMessage({
                                        defaultMessage: 'Cancel',
                                        id: 'search.action.cancel'
                                    })}
                                    onKeyDown={suggestionsLength ? null : handleTabNavigation}
                                />
                            ) : (
                                <SearchIcon boxSize="20px" />
                            )}
                        </InputRightElement>
                        <VisuallyHidden>
                            <Button
                                type="submit"
                                aria-label={intl.formatMessage({
                                    defaultMessage: 'Submit search',
                                    id: 'search.input.submit'
                                })}
                                tabIndex={'-1'}
                            />
                        </VisuallyHidden>
                    </InputGroup>
                </HStack>
            </form>
            {isSuggestionsEnabled && (
                <Stack
                    position={'absolute'}
                    left={0}
                    right={0}
                    top={`100%`}
                    zIndex={9999}
                    width={'100%'}
                    background={'rgba(0,0,0,0.2)'}
                    display={isSuggestionsOpen ? 'flex' : 'none'}
                    direction={'row'}
                    height={`${distance}px`}
                    alignItems={'flex-start'}
                    justifyContent={'flex-start'}
                    overflow={'hidden'}
                    gap={0}
                    onClick={onClose}
                >
                    <Flex
                        background="white"
                        left={0}
                        right={0}
                        width={{base: '100%', lg: '50%'}}
                        height={{base: `${distance}px`, lg: 'auto'}}
                        overflowY={suggestionsLength ? 'auto' : 'hidden'}
                        p={'20px'}
                        position={'absolute'}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {searchSuggestion.isLoading ? (
                            <Box maxHeight={'200px'} height={`${distance}px`}>
                                <LoadingSpinner wrapperStyles={{background: 'white'}} />
                            </Box>
                        ) : !isXssError && suggestionsLength ? (
                            <>
                                <VisuallyHidden aria-live="assertive">
                                    {`${suggestionsLength} ${intl.formatMessage({
                                        defaultMessage: 'suggestions available',
                                        id: 'search.available_suggestions'
                                    })}`}
                                </VisuallyHidden>
                                <SearchSuggestions
                                    suggestions={searchSuggestions}
                                    redirect={() => {
                                        onClose()
                                    }}
                                    handleTabNavigation={handleTabNavigation}
                                />
                            </>
                        ) : (
                            <Text variant={'bodySmall'}>
                                {intl.formatMessage({
                                    defaultMessage: 'No suggestions',
                                    id: 'search.empty.message'
                                })}
                            </Text>
                        )}
                    </Flex>
                </Stack>
            )}
        </Box>
    )
}

Search.propTypes = {
    isSuggestionsEnabled: PropTypes.bool,
    isFocusOnInit: PropTypes.bool,
    onClose: PropTypes.func,
    containerProps: PropTypes.object
}
Search.displayName = 'SearchInput'

export default Search
